import {
  AgxBodyText,
  AgxRow,
  AgxDiagonalUpArrowIcon,
  AgxCircleIcon,
} from '@urbanx/agx-ui-components'
import dayjs from 'dayjs'
import advancedFormat from 'dayjs/plugin/advancedFormat'
import {
  useAgentsInAgency,
  useSelectedCampaign,
  useSelectedContract,
} from 'hooks'
import { cleanTwoLineAddress } from 'helpers/address'
import ContractImage from './components/ContractImage'
import CampaignImage from './components/CampaignImage'
import { ListingCampaignModel } from 'types/rex'
import { useQuery } from '@tanstack/react-query'
import GetListingByCampaignId from 'services/functions/rex/getListingByCampaignId'
import './CampaignSectionHeader.scss'
import { useContext, useEffect, useMemo, useRef, useState } from 'react'
import { AuthContext } from '@urbanx/agx.auth.client'
import {
  AgxAvatarLabelGroup,
  AgxBadge,
  BadgeColor,
  AgxTypography,
  AgxSpacer,
  AgxDivider,
} from '@urbanx/agx.ui-components2'
import { MenuTitles, Pages, SELECTED_CAMPAIGN_TOP_MENU } from 'constants/menu'
import { SideMenu } from 'types/commonTypes'
import { useDoesUserHasRequiredRoles } from 'helpers/doesUserHaveRequiredRoles'
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom'
import slugify from 'slugify'
import {
  LaunchDetails,
  LaunchDetailsStatus,
} from '@urbanx/agx.campaigns.client'
import { useGetUrbanXTeamForAgency } from '@urbanx/agx.agencies.client'
import { useContentScroll } from 'contexts'
import clsx from 'clsx'

dayjs.extend(advancedFormat)
const TOP_MENU_FIXED_OFFSET = 80
const CamapignHeader = () => {
  const { agencyId, campaignId } = useParams()
  const navigate = useNavigate()
  const location = useLocation()
  const topMenuContainerRef = useRef<HTMLDivElement>(null)
  const { campaign: selectedCampaign } = useSelectedCampaign()
  const { contract: selectedContract } = useSelectedContract()
  const [launchDateDetails, setLaunchDateDetails] = useState<
    LaunchDetails | undefined
  >()
  const [isTopMenuFixed, setIsTopMenuFixed] = useState(false)
  const { data: agents } = useAgentsInAgency()
  const { getAuthToken } = useContext(AuthContext)
  const doesUserHaveRequiredRoles = useDoesUserHasRequiredRoles()
  const { contentScrollPosition } = useContentScroll()
  const { data: urbanXTeamForAgency } = useGetUrbanXTeamForAgency(
    agencyId ?? '',
    !!agencyId
  )

  const yesterday = new Date()
  yesterday.setDate(yesterday.getDate() - 1)

  const { data: rexListing, isLoading: rexListingisIsLoading } = useQuery<
    ListingCampaignModel | undefined
  >({
    queryKey: ['getRexListingForCampaign', selectedCampaign?.campaignId],
    queryFn: (queryInfo) => GetListingByCampaignId(queryInfo, getAuthToken),
    enabled: !!selectedCampaign?.campaignId,
  })

  const [addressLineOne, addressLineTwo] = cleanTwoLineAddress(
    selectedCampaign?.address
  )

  const activeTopMenuFromPath = useMemo(() => {
    if (!campaignId || !location.pathname) return undefined
    const campaignPath = `/campaigns/${campaignId}/`
    const afterCampaign = location.pathname.split(campaignPath)[1] ?? ''
    const firstSegment = afterCampaign.split('/')[0] ?? ''
    if (
      firstSegment === Pages.FINANCE ||
      afterCampaign.startsWith(`${Pages.FINANCE}/`)
    ) {
      return MenuTitles.FINANCE
    }
    if (
      firstSegment === Pages.CONTRACTS ||
      afterCampaign.startsWith(`${Pages.CONTRACTS}/`)
    ) {
      return MenuTitles.CONTRACTS
    }
    if (firstSegment === Pages.ACTIVITY) return MenuTitles.ACTIVITY
    if (firstSegment === Pages.FILES) return MenuTitles.FILES
    if (
      firstSegment === Pages.PROPERTY_INFORMATION ||
      firstSegment === Pages.PROPERTY_INFORMATION_SECTION ||
      firstSegment === Pages.CAMPAIGN_OVERVIEW ||
      firstSegment === Pages.CAMPAIGN_TASKS ||
      firstSegment === Pages.CAMPAIGN_MARKETING ||
      firstSegment === Pages.PERMISSIONS
    ) {
      return MenuTitles.CAMPAIGN
    }
    return undefined
  }, [campaignId, location.pathname])

  const generateImage = () => {
    if (selectedContract) {
      return (
        <ContractImage
          imageUrl={
            selectedCampaign?.propertyImageUrls?.large ??
            selectedCampaign?.propertyImageUrls?.medium
          }
        />
      )
    } else {
      return <CampaignImage selectedCampaign={selectedCampaign} />
    }
  }

  const generatePageTitle = () => {
    if (selectedContract) {
      return (
        <AgxSpacer direction="vertical" fill>
          <AgxTypography
            id={'title'}
            type={'heading'}
            size={'2x-large'}
            color="var(--text-primary)"
          >
            {selectedContract.contractName}
          </AgxTypography>
          <AgxBodyText large>
            {addressLineOne ? (
              <>
                {addressLineOne}, {addressLineTwo}
              </>
            ) : (
              <>Address Unidentified</>
            )}
          </AgxBodyText>
        </AgxSpacer>
      )
    } else {
      return (
        <AgxSpacer direction="vertical" fill gap={'none'}>
          <AgxTypography
            id={'title'}
            type={'heading'}
            size={'2x-large'}
            color="var(--text-primary)"
          >
            {addressLineOne ? (
              <>
                {addressLineOne}, {addressLineTwo}
              </>
            ) : (
              <>Address Unidentified</>
            )}
          </AgxTypography>
        </AgxSpacer>
      )
    }
  }

  const generateLink = (pageLink: string) => {
    if (pageLink === '') return '/'

    if (agencyId) {
      return `/${Pages.AGENCIES}/${agencyId}/campaigns/${campaignId}/${pageLink}`
    }
    return pageLink || '#'
  }

  const renderTopMenu = (menuItems: SideMenu[], activeMenu?: MenuTitles) => {
    return menuItems
      .filter((menu) => !menu.isHidden)
      .filter((menu) => doesUserHaveRequiredRoles(menu.requiredRoles))
      .filter((menu) => menu.includeOnlyInState.length === 0)
      .map((menu, index) => (
        <li
          onClick={() => {
            navigate(generateLink(menu.pageLink ?? ''), {
              state: { activeTab: menu.title },
            })
          }}
          key={`sideMenu_${index}`}
          className={`secondaryMenuItemTopMenu ${
            activeMenu && activeMenu === menu.title ? 'active' : ''
          }`}
        >
          <Link
            data-testid={`link-${slugify(menu.title)}`}
            to={generateLink(menu.pageLink ?? '')}
          >
            {menu.title}
          </Link>
        </li>
      ))
  }
  useEffect(() => {
    const details = selectedCampaign?.launchDateDetails
    if (!details?.status) {
      setLaunchDateDetails(undefined)
      return
    }

    setLaunchDateDetails({
      launchDate: details.launchDate,
      status: details.status as unknown as LaunchDetailsStatus,
    })
  }, [selectedCampaign?.launchDateDetails])

  useEffect(() => {
    if (!topMenuContainerRef.current) {
      setIsTopMenuFixed(false)
      return
    }

    setIsTopMenuFixed(
      topMenuContainerRef.current.getBoundingClientRect().top <=
        TOP_MENU_FIXED_OFFSET
    )
  }, [contentScrollPosition])

  const generateListingAgents = () => {
    const listingAgents = []

    if (selectedCampaign?.leadAgentId) {
      const leadAgent = agents?.find(
        (agent) => agent.id === selectedCampaign.leadAgentId
      )

      if (leadAgent) {
        listingAgents.push(
          <AgxAvatarLabelGroup
            id={`avatar-${leadAgent.name?.firstName}`}
            key={leadAgent.id}
            type="avatar"
            variant="round"
            photoUrl={leadAgent.photoUrl}
            label1={`${leadAgent.name?.firstName} ${leadAgent.name?.lastName}`}
            size="small"
          />
        )
      }
    }

    if (selectedCampaign?.secondAgentId) {
      const secondAgent = agents?.find(
        (agent) => agent.id === selectedCampaign.secondAgentId
      )

      if (secondAgent && secondAgent?.softDeleted === false) {
        listingAgents.push(
          <AgxAvatarLabelGroup
            id={`avatar-${secondAgent.name?.firstName}`}
            key={secondAgent.id}
            type="avatar"
            variant="round"
            photoUrl={secondAgent.photoUrl}
            label1={`${secondAgent.name?.firstName} ${secondAgent.name?.lastName}`}
            size="small"
          />
        )
      }
    }

    if (urbanXTeamForAgency?.adminManager) {
      const adminManager = urbanXTeamForAgency?.adminManager
      listingAgents.push(
        <AgxSpacer
          key={`adminManager-${adminManager.email}`}
          direction="horizontal"
          gap={'3xs'}
          contentAlignment="center"
        >
          <AgxAvatarLabelGroup
            id={`avatar-${adminManager.firstName}`}
            type="avatar"
            variant="round"
            photoUrl={adminManager.photo?.publicPath}
            label1={`${adminManager.firstName} ${adminManager.lastName}`}
            size="small"
          />
          <AgxTypography
            id={`lblAdminManager`}
            type={'title'}
            size={'small'}
            color="var(--text-tertiary)"
          >
            {`(Admin manager)`}
          </AgxTypography>
        </AgxSpacer>
      )
    }

    return (
      <AgxSpacer direction="horizontal" gap={'sm'}>
        {listingAgents}
      </AgxSpacer>
    )
  }

  const generateListingBadges = () => {
    const listingBadges = []

    listingBadges.push(
      <AgxBadge
        key="ListingBadgeBlankContractIsLive"
        type="colourfill"
        size="md"
        colour={selectedCampaign?.blankContractIsLive ? 'green' : 'red'}
        text={
          selectedCampaign?.blankContractIsLive
            ? 'Contract Live'
            : 'Contract Offline'
        }
      />
    )

    if (launchDateDetails) {
      if (
        launchDateDetails.status === LaunchDetailsStatus.Confirmed &&
        launchDateDetails.launchDate
      ) {
        const date = dayjs(launchDateDetails.launchDate)
        const today = dayjs()
        let status: 'Today' | 'Overdue' | 'Future' = 'Future'
        if (date.isSame(today, 'day')) {
          status = 'Today'
        } else if (date.isBefore(today, 'day')) {
          status = 'Overdue'
        }
        const colour = (status === 'Overdue' ? 'red' : 'yellow') as BadgeColor
        const text = `Launch ${date.format('Do MMM')}`

        if (status === 'Today' || status === 'Overdue') {
          listingBadges.push(
            <AgxBadge
              key="ListingBadgeLaunchDate"
              type="highlighted"
              colour={colour}
              size="md"
              text={text}
              highlightText={status}
            />
          )
        } else {
          listingBadges.push(
            <AgxBadge
              key="ListingBadgeLaunchDate"
              type="colourfill"
              colour={colour}
              size="md"
              text={text}
            />
          )
        }
      } else {
        listingBadges.push(
          <AgxBadge
            key="ListingBadgeLaunchDateNotSet"
            type="highlighted"
            colour="lightgrey"
            size="md"
            text={
              launchDateDetails.status === LaunchDetailsStatus.OffMarket
                ? 'Off Market'
                : 'Launch date not set'
            }
          />
        )
      }
    }

    if (!rexListingisIsLoading) {
      listingBadges.push(
        <AgxBadge
          key="ListingBadgeRexLink"
          type="colourfill"
          size="md"
          colour="lightgrey"
          text={rexListing ? 'REX ' + rexListing.listingId : 'REX disconnected'}
          leftIcon={
            rexListing ? (
              <AgxCircleIcon color="#54CF09" />
            ) : (
              <AgxCircleIcon color="#DFDFDF" />
            )
          }
          link={
            rexListing
              ? `https://app.rexsoftware.com/listings/#id=${encodeURIComponent(
                  rexListing.listingId
                )}&_account_id=${encodeURIComponent(rexListing.brandAccountId)}`
              : undefined
          }
          rightIcon={<AgxDiagonalUpArrowIcon color="#000" />}
        />
      )
    }

    return (
      <AgxRow mediumGap fill>
        {listingBadges}
      </AgxRow>
    )
  }

  return (
    <section>
      <AgxRow fill extraClasses="contractCard">
        {generateImage()}
        <AgxSpacer fill gap={'sm'}>
          {generatePageTitle()}
          {generateListingAgents()}
          {generateListingBadges()}
        </AgxSpacer>
      </AgxRow>
      <div ref={topMenuContainerRef}>
        <AgxRow
          extraClasses={clsx(
            'topMenuContainer',
            isTopMenuFixed ? 'topMenuContainerFixed' : ''
          )}
        >
          <AgxSpacer direction="vertical" gap={'xs'}>
            <ul className="secondaryMenuContainer horizontal">
              {renderTopMenu(
                SELECTED_CAMPAIGN_TOP_MENU,
                location.state?.activeTab ??
                  activeTopMenuFromPath ??
                  MenuTitles.CAMPAIGN
              )}
            </ul>
            {isTopMenuFixed && (
              <>
                <div className="topMenuMask1"></div>
                <div className="topMenuMask2">
                  <AgxDivider variant={'vertical'} />
                </div>
              </>
            )}
          </AgxSpacer>
        </AgxRow>
      </div>
    </section>
  )
}

export default CamapignHeader
